import { useState, type FormEvent } from 'react';
import { Mail, ShieldCheck } from 'lucide-react';
import { requireSupabase } from '@/lib/supabase';

type Status =
  { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent' } | { kind: 'error'; message: string };

/** Supabase manda 6 dígitos por defecto; el panel permite hasta 10. */
const CODE_RE = /^\d{6,10}$/;

/**
 * Acceso solo por invitación (SPEC 10).
 *
 * `shouldCreateUser: false` es el segundo cerrojo: `signInWithOtp` da de alta
 * usuarios nuevos por defecto, así que desactivar el registro en el panel de
 * Supabase no basta si alguien vuelve a activarlo sin darse cuenta.
 */
export function LoginScreen() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setStatus({ kind: 'sending' });
    try {
      const { error } = await requireSupabase().auth.signInWithOtp({
        email: trimmed,
        options: { shouldCreateUser: false },
      });
      if (error) {
        setStatus({
          kind: 'error',
          // No se distingue "no invitado" de otros fallos a propósito: decirlo
          // permitiría averiguar desde fuera quién tiene cuenta.
          message: 'No hemos podido enviar el enlace. Revisa el correo o pide una invitación.',
        });
        return;
      }
      setStatus({ kind: 'sent' });
    } catch {
      setStatus({
        kind: 'error',
        message: 'No hay conexión con el servidor. Inténtalo más tarde.',
      });
    }
  }

  if (status.kind === 'sent') {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 text-center">
          <Mail aria-hidden="true" className="size-10 text-[var(--color-brand-strong)]" />
          <h1 className="text-xl font-bold">Revisa tu correo</h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            Te hemos enviado un enlace y un código a <strong>{email.trim()}</strong>. Caducan en una
            hora.
          </p>
        </div>
        <CodeForm email={email.trim()} />
        <button
          type="button"
          onClick={() => setStatus({ kind: 'idle' })}
          className="tap mx-auto mt-2 block rounded-lg px-3 text-sm font-medium text-[var(--color-brand-strong)] underline"
        >
          Usar otro correo
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <ShieldCheck aria-hidden="true" className="size-10 text-[var(--color-brand-strong)]" />
          <h1 className="text-xl font-bold">NutriGym</h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            El acceso es solo por invitación. Escribe el correo con el que te invitaron.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-medium">
            Correo electrónico
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={status.kind === 'error' ? 'login-error' : undefined}
            aria-invalid={status.kind === 'error'}
            className="tap rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-base"
          />
        </div>

        {status.kind === 'error' ? (
          <p id="login-error" role="alert" className="text-sm text-[var(--color-danger)]">
            {status.message}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status.kind === 'sending'}
          className="tap rounded-xl bg-[var(--color-brand-button)] px-4 font-semibold text-[var(--color-on-brand)] disabled:opacity-60"
        >
          {status.kind === 'sending' ? 'Enviando…' : 'Enviar enlace de acceso'}
        </button>
      </form>
    </Shell>
  );
}

/**
 * Entrar con el código del correo en vez de con el enlace.
 *
 * En iPhone, el enlace del correo se abre en Safari, que NO comparte
 * almacenamiento con la app instalada: la sesión quedaba en Safari y la app se
 * quedaba en esta pantalla para siempre. El código se teclea aquí mismo, así
 * que la sesión nace dentro de la app.
 */
function CodeForm({ email }: { email: string }) {
  const [code, setCode] = useState('');
  const [state, setState] = useState<'idle' | 'checking' | 'wrong'>('idle');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const token = code.replace(/\s/g, '');
    if (!CODE_RE.test(token)) return;

    setState('checking');
    try {
      const { error } = await requireSupabase().auth.verifyOtp({ email, token, type: 'email' });
      // Si va bien, `onAuthStateChange` trae la sesión y esta pantalla desaparece.
      setState(error ? 'wrong' : 'idle');
    } catch {
      setState('wrong');
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="mt-5 flex flex-col gap-2">
      <label htmlFor="codigo" className="text-sm font-medium">
        ¿Tienes la app instalada en el iPhone? Escribe aquí el código
      </label>
      <input
        id="codigo"
        name="codigo"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={12}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        aria-invalid={state === 'wrong'}
        aria-describedby={state === 'wrong' ? 'codigo-error' : undefined}
        className="tap rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-center text-lg tracking-[0.3em] tabular"
      />
      {state === 'wrong' ? (
        <p id="codigo-error" role="alert" className="text-sm text-[var(--color-danger)]">
          El código no es válido o ha caducado. Pide otro con «Usar otro correo».
        </p>
      ) : null}
      <button
        type="submit"
        disabled={state === 'checking' || !CODE_RE.test(code.replace(/\s/g, ''))}
        className="tap rounded-xl bg-[var(--color-brand-button)] px-4 font-semibold text-[var(--color-on-brand)] disabled:opacity-60"
      >
        {state === 'checking' ? 'Comprobando…' : 'Entrar con el código'}
      </button>
    </form>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center px-6">
      <div className="w-full max-w-sm">{children}</div>
    </div>
  );
}
